import React, { useEffect } from "react";
import { Modal } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import ReactDatePicker from "react-datepicker";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import JsonCollapse from "../JsonCollapse";
import timezones from "../../nodes/Logic/timezones";
import Input from "../../nodes/Logic/Inputs/Input";
import UsedInLink from "../UsedInLink";
import {
  addNewBusinessHours,
  selectBusinessHours,
  setBusinessHours as setBH,
} from "../../../store/slices/projectSlice";

export default function BusinessHours() {
  const navigate = useNavigate();
  const params = useParams();

  const businessHours = useSelector(
    selectBusinessHours(params.businessHoursUUID)
  );
  const dispatch = useDispatch();

  const setBusinessHours = (fn) => dispatch(setBH(fn(businessHours)));

  const location = useLocation();
  const handleClose = () => {
    if (businessHours?.Label) {
      navigate(location.state?.nextPage || `/library/logic/builder/${params.projectID}`);
    } else {
      toast.error("Label is missing!");
    }
  };

  useEffect(() => {
    // set businessHours when the window shows
    if (params.businessHoursUUID) {
      if (params.businessHoursUUID === "create_new") {
        const newBHUUID = uuidv4();
        dispatch(addNewBusinessHours({ UUID: newBHUUID }));
        navigate(`/library/logic/builder/${params.projectID}/business-hours/${newBHUUID}`, {
          replace: true,
        });
      }
    }
  }, [params.businessHoursUUID]);

  return (
    <Modal
      size="xl"
      centered
      show={params.businessHoursUUID && businessHours}
      onHide={handleClose}
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="icon fa fa-edit" />
          {params.businessHoursUUID === "create_new" ? "Create " : "Edit "}
          business hours
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row mb-3">
          <div className="col-sm">
            <label className="form-label">Business hours label</label>
            <input
              className="form-control mb-3"
              title="Business hours label"
              placeholder="Business hours"
              type="text"
              value={businessHours?.Label ?? ""}
              onChange={(e) =>
                setBusinessHours((prev) => ({
                  ...prev,
                  Label: e.target.value,
                }))
              }
            />
          </div>
          <div className="col-sm">
            <label className="form-label">Time zone</label>
            <Input
              inputType="select"
              isValidNewOption={() => false}
              value={[businessHours?.TimeZone || ""]}
              onChange={(values) =>
                setBusinessHours((prev) => ({
                  ...prev,
                  TimeZone: values[0],
                }))
              }
              options={[
                {
                  value: "",
                  label: "Project default time zone",
                },
                ...timezones.map((tz) => ({
                  value: tz.tzCode,
                  label: tz.label,
                })),
              ]}
            />
          </div>
        </div>

        {[
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ].map((day, index) => (
          <div key={day} className="row mb-1">
            <div className="col-1">{day}</div>
            <div className="col-3">
              <select
                className="form-select"
                aria-label="Default select example"
                value={businessHours?.[day]?.Business || "hours"}
                onChange={(e) =>
                  setBusinessHours((prev) => ({
                    ...prev,
                    [day]: {
                      ...prev[day],
                      Business: e.target.value,
                    },
                  }))
                }
              >
                <option value="all-day">All day</option>
                <option value="no-business">No business</option>
                <option value="hours">Business hours</option>
              </select>
            </div>
            {["Start", "End"].map((time) => (
              <div className="col-2" key={`${day}-${time}`}>
                <ReactDatePicker
                  className="form-control"
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  dateFormat="h:mm a"
                  disabled={businessHours?.[day]?.Business !== "hours"}
                  selected={
                    new Date(
                      new Date().setHours(
                        ...(businessHours?.[day]?.[time] || "12:00").split(":")
                      )
                    )
                  }
                  onChange={(date) => {
                    if (
                      time === "Start" &&
                      date >
                        new Date(
                          new Date().setHours(
                            ...(businessHours?.[day]?.End || "12:00").split(":")
                          )
                        )
                    ) {
                      toast.error("Start cannot be after end.");
                      return;
                    }
                    if (
                      time === "End" &&
                      date <
                        new Date(
                          new Date().setHours(
                            ...(businessHours?.[day]?.Start || "12:00").split(
                              ":"
                            )
                          )
                        )
                    ) {
                      toast.error("End cannot be before start.");
                      return;
                    }
                    setBusinessHours((prev) => ({
                      ...prev,
                      [day]: {
                        ...prev[day],
                        [time]: `${date
                          .getHours()
                          .toString()
                          .padStart(2, "0")}:${date
                          .getMinutes()
                          .toString()
                          .padStart(2, "0")}`,
                      },
                    }));
                  }}
                />
              </div>
            ))}
            {index === 0 && (
              <div className="col">
                <button
                  className="btn btn-link align-baseline"
                  type="button"
                  onClick={() => {
                    const value = businessHours?.Monday;
                    setBusinessHours((prev) => ({
                      ...prev,
                      Monday: value,
                      Tuesday: value,
                      Wednesday: value,
                      Thursday: value,
                      Friday: value,
                      Saturday: value,
                      Sunday: value,
                    }));
                  }}
                >
                  copy to all
                </button>
              </div>
            )}
          </div>
        ))}

        <UsedInLink arg={businessHours} />
        <JsonCollapse json={businessHours} />
      </Modal.Body>
    </Modal>
  );
}
